import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useSelector } from 'react-redux';
import useApi from '../../../services/useApi';
import endpoints from '../../../services/endpoints';
import { convertTimestamp } from '../../../components/commonfunctions/convertDate';

// TypeScript interfaces for ZoodPay Money dashboard data
interface MNOData {
  mno_name: string;
  amount: number;
}

interface ZoodPayMoneyData {
  total_receivable?: number;
  total_payable?: number;
  payables_for_mno?: MNOData[];
  receivables_from_mno?: MNOData[];
  last_updated?: string;
}

// Helper function to format AFA amounts
function formatAfaAmount(amount: number | string | undefined | null) {
  const num = Number(amount || "0.00");
  if (isNaN(num)) return "AFA 0.00";
  if (num < 0) {
    return `AFA (-) ${Math.abs(num).toFixed(2)}`;
  }
  return `AFA ${num.toFixed(2)}`;
}

// Card component for displaying financial metrics
const FinancialCard: React.FC<{
  title: string;
  amount: string;
  className?: string;
}> = ({ title, amount, className = "" }) => (
  <motion.div
    className={`bg-p_light rounded-2xl border border-outline p-6 w-full ${className}`}
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
  >
    <h3 className="text-p_primary text-lg font-semibold mb-2">{title}</h3>
    <p className="text-primary_text text-lg font-semibold">{amount}</p>
  </motion.div>
);

// MNO List component for displaying MNO-specific data
const MNOListCard: React.FC<{
  title: string;
  data: Array<{ mno_name: string; amount: number }>;
  className?: string;
}> = ({ title, data, className = "" }) => (
  <motion.div
    className={`bg-p_light rounded-2xl border border-outline p-6 w-full ${className}`}
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
  >
    <h3 className="text-p_primary text-lg font-semibold mb-4">{title}</h3>
    <div className="space-y-3">
      {data.length > 0 ? (
        data.map((item, index) => (
          <div key={index} className="flex justify-between items-center">
            <span className="text-primary_text font-semibold">{item.mno_name}</span>
            <span className="text-primary_text font-semibold">
              {formatAfaAmount(item.amount)}
            </span>
          </div>
        ))
      ) : (
        <p className="text-gray-500 text-center py-4">No data available</p>
      )}
    </div>
  </motion.div>
);

const MoneyDashboard: React.FC = () => {
  const { getAPI } = useApi();
  const [dashboardData, setDashboardData] = useState<ZoodPayMoneyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Get current user information from Redux (already stored during login)
  const userInfo = useSelector((state: { userEmail: { userInfo: { user_code?: string } } }) => state.userEmail.userInfo);
  const currentUserCode = userInfo.user_code || '';

  // Fetch ZoodPay Money dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await getAPI(endpoints.zoodpayMoneyDashboard);
        
        if (response.error) {
          if (response.data?.request?.status === 403 || response.data.request?.message?.includes('403') || response.data.request?.message?.includes('Forbidden')) {
            setDashboardData({
              last_updated: undefined,
              total_receivable: undefined,
              total_payable: undefined,
              payables_for_mno: undefined,
              receivables_from_mno: undefined,
            });
            return;
          }
          setError('Failed to fetch dashboard data');
          return;
        }
        
        setDashboardData(response.data.data);
      } catch (err) {
        setError('An error occurred while fetching data');
        console.error('Error fetching dashboard data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - getAPI is stable from useApi hook

  if (loading) {
    return (
      <>
        {/* Header Skeleton */}
        <div className="w-full flex flex-row pt-8 pb-4 px-4 justify-between items-center">
          <div className="h-8 bg-gray-200 rounded-sm w-48 animate-pulse"></div>
          <div className="h-4 bg-gray-200 rounded-sm w-32 animate-pulse"></div>
        </div>

        {/* Dashboard Cards Skeleton */}
        <div className="w-full px-6 py-2">
          <div className="flex flex-col border border-gray-100 w-full gap-6 rounded-2xl px-1 py-5">
            {/* Financial Cards Row Skeleton - Show skeleton for potential financial metrics */}
            <div className="w-full grid grid-cols-1 sm:grid-cols-2 px-4 gap-4">
              <div className="bg-p_light rounded-2xl border border-outline p-6 w-full">
                <div className="animate-pulse">
                  <div className="h-6 bg-gray-200 rounded-sm w-3/4 mb-2"></div>
                  <div className="h-8 bg-gray-200 rounded-sm w-1/2"></div>
                </div>
              </div>
              <div className="bg-p_light rounded-2xl border border-outline p-6 w-full">
                <div className="animate-pulse">
                  <div className="h-6 bg-gray-200 rounded-sm w-3/4 mb-2"></div>
                  <div className="h-8 bg-gray-200 rounded-sm w-1/2"></div>
                </div>
              </div>
            </div>

            <div className="w-full grid grid-cols-1 sm:grid-cols-2 px-4 gap-4">
              <div className="bg-p_light rounded-2xl border border-outline p-6 w-full">
                <div className="animate-pulse">
                  <div className="h-6 bg-gray-200 rounded-sm w-3/4 mb-4"></div>
                  <div className="space-y-3">
                    {[...Array(3)].map((_, index) => (
                      <div key={index} className="flex justify-between items-center">
                        <div className="h-4 bg-gray-200 rounded-sm w-24"></div>
                        <div className="h-4 bg-gray-200 rounded-sm w-20"></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="bg-p_light rounded-2xl border border-outline p-6 w-full">
                <div className="animate-pulse">
                  <div className="h-6 bg-gray-200 rounded-sm w-3/4 mb-4"></div>
                  <div className="space-y-3">
                    {[...Array(3)].map((_, index) => (
                      <div key={index} className="flex justify-between items-center">
                        <div className="h-4 bg-gray-200 rounded-sm w-24"></div>
                        <div className="h-4 bg-gray-200 rounded-sm w-20"></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-500 text-lg mb-2">Error loading dashboard</p>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500 text-lg">No data available</p>
      </div>
    );
  }

  // Check if user is super admin (user_code starts with SADM)
  const isSuperAdmin = currentUserCode.startsWith('SADM');
  
  // Check if any data exists to show the money section
  // For super admin, always show financial data section (even if payables doesn't exist)
  const hasFinancialData = dashboardData.total_receivable !== undefined || 
                          dashboardData.total_payable !== undefined || 
                          isSuperAdmin;
  const hasMNOData = (dashboardData.payables_for_mno) || 
                     (dashboardData.receivables_from_mno);
  const hasAnyData = hasFinancialData || hasMNOData;

  // If no data exists, don't show the money section at all
  if (!hasAnyData) {
    return null;
  }

  return (
    <>
      {/* Last Updated Info */}
      <div className="w-full flex flex-wrap gap-2 pt-8 pb-4 px-4 justify-between items-center">
        <p className="text-2xl font-semibold text-primary_text">ZoodPay Money</p>
        <p className="text-sm text-gray-600">
          Last updated on: {dashboardData.last_updated ? convertTimestamp(dashboardData.last_updated) : '-'}
        </p>
      </div>

      {/* Dashboard Cards Grid */}
      <div className="w-full px-4 py-2">
      <div className="flex flex-col border border-gray-100 w-full gap-6 rounded-2xl px-1 py-5">
         {/* Financial Cards Row */}
         {hasFinancialData && (
           <div className='w-full grid grid-cols-1 sm:grid-cols-[30%_1fr] px-4 gap-4'>
             {dashboardData.total_receivable !== undefined && (
               <FinancialCard
                 title="Total Receivables"
                 amount={formatAfaAmount(dashboardData.total_receivable)}
                 className={dashboardData.total_payable === undefined ? "sm:col-span-2" : ""}
               />
             )}
             {(dashboardData.total_payable !== undefined || isSuperAdmin) && (
               <FinancialCard
                 title="Total Payables"
                 amount={dashboardData.total_payable !== undefined ? formatAfaAmount(dashboardData.total_payable) : "-"}
                 className={dashboardData.total_receivable === undefined ? "sm:col-span-2" : ""}
               />
             )}
           </div>
         )}

         {/* MNO List Cards Row */}
         {hasMNOData && (
           <div className="w-full grid grid-cols-1 sm:grid-cols-[70%_1fr] px-4 gap-4">
             {dashboardData.payables_for_mno && (
               <MNOListCard
                 title="Payables for MNO"
                 data={dashboardData.payables_for_mno}
                 className={!(dashboardData.receivables_from_mno) ? "sm:col-span-2" : ""}
               />
             )}
             {dashboardData.receivables_from_mno && (
               <MNOListCard
                 title="Receivables from MNO"
                 data={dashboardData.receivables_from_mno}
                 className={!(dashboardData.payables_for_mno) ? "sm:col-span-2" : ""}
               />
             )}
           </div>
         )}
      </div>
    </div>
    </>
  );
};

export default MoneyDashboard;

